import React from 'react';
import { estimarPrecio, getCluster } from '../../utils/pricingMTO';

const TIPO_LABELS = {
  termo_electrico:     'Termo eléctrico',
  conductos:           'Conductos',
  caldera:             'Caldera',
  aerotermo_conductos: 'Aerotermo + Conductos',
  aerotermo:           'Aerotermo (solo ACS)',
  aerotermo_split:     'Aerotermo + Split',
  split:               'Split A/C',
};

const LITRAJE_OPTIONS = [
  { id: 'hasta_110', label: '≤ 110 L' },
  { id: '150_200',   label: '150–200 L' },
  { id: '250_mas',   label: '≥ 250 L' },
];

// Precios estimados para tipos sin datos de transacciones reales
// Basados en la estructura de precios observada en la zona
const ESTIMACIONES_GENERALES = {
  aerotermo:     { min: 130, max: 190 },
  aerotermo_split: { min: 190, max: 280 },
  split:         { min: 89,  max: 150 },
};

const PrecioEstimadoMTO = ({ formData, updateFormData }) => {
  const { tipoEquipoMantenimiento, codigoPostal, litrajeTermoMTO, modelo } = formData;

  if (!tipoEquipoMantenimiento) return null;
  if (!TIPO_LABELS[tipoEquipoMantenimiento]) return null;

  const tipoLabel = TIPO_LABELS[tipoEquipoMantenimiento];

  // Tipos con datos reales de precio
  const tienesDatosReales = !['aerotermo', 'aerotermo_split', 'split'].includes(tipoEquipoMantenimiento);

  let precio   = tienesDatosReales
    ? estimarPrecio(codigoPostal, tipoEquipoMantenimiento, litrajeTermoMTO, modelo)
    : null;
  const cluster  = getCluster(codigoPostal);

  // Fallback para tipos sin datos reales
  if (!precio && ESTIMACIONES_GENERALES[tipoEquipoMantenimiento]) {
    const est = ESTIMACIONES_GENERALES[tipoEquipoMantenimiento];
    precio = { min: est.min, max: est.max, clusterName: null, fromData: false };
  }

  // Para termos: si el CP tiene datos pero la capacidad no está seleccionada,
  // mostrar selector de litraje para afinar el precio
  const needsLitraje = tipoEquipoMantenimiento === 'termo_electrico' && !litrajeTermoMTO;

  if (!precio) {
    // Cluster conocido pero sin datos para este tipo
    if (cluster) {
      return (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-4 py-3 flex items-center gap-2 text-xs text-gray-500">
          <span>📍</span>
          <span>Zona: <strong>{cluster.name}</strong> — sin datos de precio para {tipoLabel.toLowerCase()}</span>
        </div>
      );
    }
    return null;
  }

  const same = precio.min === precio.max;

  return (
    <div className="bg-emerald-50 rounded-2xl border border-emerald-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-100/70 border-b border-emerald-200">
        <span className="text-sm">💶</span>
        <span className="text-xs font-bold text-emerald-800">Precio estimado — orientativo</span>
        {!precio.fromData && (
          <span className="text-xs text-emerald-500 ml-auto italic">
            {cluster ? cluster.name : 'zona no reconocida'}
          </span>
        )}
      </div>

      <div className="px-4 py-3 flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-emerald-800">{tipoLabel}</p>
          {precio.clusterName && (
            <p className="text-xs text-emerald-600 mt-0.5">📍 {precio.clusterName}</p>
          )}
          {!tienesDatosReales && (
            <p className="text-xs text-emerald-500 mt-0.5 italic">estimación general</p>
          )}
          {/* Split multi-unidad: ajuste por unidades */}
          {(tipoEquipoMantenimiento === 'split' || tipoEquipoMantenimiento === 'aerotermo_split') && formData.splitUnidades && formData.splitUnidades !== '1' && (
            <p className="text-xs text-amber-600 mt-0.5">
              +€30–50 por unidad adicional
            </p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xl font-bold text-emerald-800 leading-tight">
            {same ? `${precio.min} €` : `${precio.min}–${precio.max} €`}
          </p>
          <p className="text-xs text-emerald-500">IVA no incluido</p>
        </div>
      </div>

      {/* Selector de litraje para termos */}
      {needsLitraje && (
        <div className="border-t border-emerald-200 px-4 py-2.5">
          <p className="text-xs text-emerald-700 font-semibold mb-2">Selecciona capacidad para afinar el precio:</p>
          <div className="flex gap-2">
            {LITRAJE_OPTIONS.map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => updateFormData({ litrajeTermoMTO: opt.id })}
                className="flex-1 text-xs font-semibold py-1.5 px-2 rounded-lg border-2 border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-100 transition-colors"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Litraje seleccionado — mostrar + botón cambiar */}
      {tipoEquipoMantenimiento === 'termo_electrico' && litrajeTermoMTO && (
        <div className="border-t border-emerald-200 px-4 py-2 flex items-center justify-between">
          <p className="text-xs text-emerald-600">
            Capacidad: <strong>{LITRAJE_OPTIONS.find(o => o.id === litrajeTermoMTO)?.label}</strong>
          </p>
          <button
            type="button"
            onClick={() => updateFormData({ litrajeTermoMTO: '' })}
            className="text-xs text-emerald-500 underline"
          >
            Cambiar
          </button>
        </div>
      )}
    </div>
  );
};

export default PrecioEstimadoMTO;
